// functions/home_page_vehicle_functions.js

// Import query routes
import { repair_defect } from "../../api/defects";
import { download_monthly_checks_report, fetch_monthly_checks } from "../../api/vehicles";
import ExcelJS from 'exceljs';
import { generateFilenameWithRandomString } from "../../components/lib/functions";
import { formatCheckDataForDownload } from '../../components/lib/functions';

export class HomePageVehicles {
    constructor(showAlert) {
        this.showAlert = showAlert;
    }

    fetchMonthlyChecks = async (date, selected_vehicle_id, setIsLoading, setMonthlyVehicleChecks) => {
        setIsLoading(true);
        const { data, status } = await fetch_monthly_checks(date, selected_vehicle_id);
        if (status === 200 || status === 201) {
            setIsLoading(false);
            setMonthlyVehicleChecks(data);
        } else {
            setIsLoading(false);
            this.showAlert({
                message: data.message || data.error,
                type: 'danger',
                buttons: [
                    {
                        type: 'danger',
                        label: 'Close',
                    }
                ],
            });
        }
    }

    downloadMonthlyReport = async (date, selectedVehicleId, setIsLoading) => {
        if (!date) {
            this.showAlert({
                message: 'Please select a month and year first.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        setIsLoading(true);
        try {
            const { status, data, fileName, message } = await download_monthly_checks_report(date, selectedVehicleId || 'all');
            if (status !== 200 || !data) {
                this.showAlert({
                    message: message || 'Failed to download monthly report.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
                return;
            }

            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'Monthly_Vehicle_Checks_Report.pdf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            this.showAlert({
                message: 'Monthly report downloaded successfully!',
                type: 'success',
                buttons: [{ type: 'success', label: 'OK' }],
            });
        } catch (error) {
            console.error('Monthly report download error:', error);
            this.showAlert({
                message: 'An error occurred while downloading the monthly report.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    updateDefect = async (repair, profile_uuid, setIsLoading, closeDefects, fetchData) => {
        setIsLoading(true);
        const { status } = await repair_defect(repair, profile_uuid);
        if (status === 201 || status === 200) {
            this.showAlert({
                message: 'Defect updated successfully!',
                type: 'success',
                buttons: [
                    {
                        label: 'Close',
                    }
                ],
            });
            // Close the overlay first, then refresh data
            closeDefects();
            // Refresh the data to get updated defects list
            await fetchData();
        } else {
            this.showAlert({
                message: 'Failed to update defect',
                type: 'danger',
                buttons: [
                    {
                        type: 'danger',
                        label: 'Close',
                    }
                ],
            });
        }
        setIsLoading(false);
    }

    downloadDefects = async (monthlyVehicleChecks, setIsLoading) => {
        if (!monthlyVehicleChecks || monthlyVehicleChecks.length < 1) {
            this.showAlert({
                message: 'No data to download',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
            return;
        }
        
        // Filter checks that have defects (status !== "passed")
        const checksWithDefects = monthlyVehicleChecks.filter(check => {
            return check.check_status && check.check_status.toLowerCase() !== 'passed' && check.defects && check.defects.length > 0;
        });

        if (checksWithDefects.length === 0) {
            this.showAlert({
                message: 'No defects found in the current data',
                type: 'info',
                buttons: [{
                    type: 'info',
                    label: 'Close',
                }],
            });
            return;
        }

        setIsLoading(true);
        try {
            const fileName = generateFilenameWithRandomString('Vehicle_Defects_Report', 'xlsx');
            
            // Format defects data for export
            const defectsData = [];
            
            checksWithDefects.forEach(check => {
                check.defects.forEach(defect => {
                    defectsData.push({
                        'Date': check.date,
                        'Driver': check.name,
                        'Vehicle Reg': check.reg,
                        'Mileage': check.mileage,
                        'Defect Type': defect.defect_type_name,
                        'Comments': defect.comments,
                        'Repairer': defect.repairer_name || 'Not Assigned',
                        'Status': defect.status
                    });
                });
            });

            // Create a new workbook and worksheet using ExcelJS
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Vehicle Defects Report');
            
            if (defectsData.length === 0) {
                throw new Error('No defects data to export');
            }
            
            // Get headers from the first row of data
            const headers = Object.keys(defectsData[0]);
            
            // Add headers to worksheet
            const headerRow = worksheet.addRow(headers);
            
            // Style the header row with grey background
            headerRow.eachCell((cell, colNumber) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFD3D3D3' } // Grey background
                };
                cell.font = {
                    bold: true,
                    color: { argb: 'FF000000' } // Black text
                };
                cell.alignment = {
                    horizontal: 'center',
                    vertical: 'middle',
                    wrapText: true
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } }
                };
            });
            
            // Add data rows
            defectsData.forEach((rowData, rowIndex) => {
                const row = worksheet.addRow(Object.values(rowData));
                
                // Determine color based on defect status
                const status = String(rowData.Status || '').toLowerCase().trim();
                let rowFillColor = null;
                
                if (status === 'failed') {
                    rowFillColor = 'FFFFE6E6'; // Light red for failed defects
                } else if (status === 'repaired') {
                    rowFillColor = null; // Clear/transparent for repaired defects
                } else if (status === 'in progress' || status === 'pending') {
                    rowFillColor = 'FFFFF3CD'; // Light yellow for in progress
                } else if (rowIndex % 2 === 0) {
                    rowFillColor = 'FFF8F9FA'; // Very light grey for even rows
                }
                
                // Style each cell in the data row
                row.eachCell((cell, colNumber) => {
                    // Base styling for all data cells
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                        left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                        bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                        right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
                    };
                    
                    // Apply row-level background color only if not repaired
                    if (rowFillColor) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: rowFillColor }
                        };
                    }
                    
                    // Default alignment
                    cell.alignment = {
                        horizontal: 'left',
                        vertical: 'middle',
                        wrapText: true
                    };
                    
                    // Status-based text formatting
                    if (status === 'failed') {
                        cell.font = {
                            color: { argb: 'FFCC0000' }, // Dark red text for failed
                            bold: true
                        };
                    } else if (status === 'repaired') {
                        cell.font = {
                            color: { argb: 'FF000000' }, // Normal black text for repaired
                            bold: false
                        };
                    }
                });
            });
            
            // Auto-fit columns
            worksheet.columns.forEach((column, index) => {
                let maxLength = headers[index] ? headers[index].length : 10;
                defectsData.forEach(row => {
                    const value = Object.values(row)[index];
                    if (value && String(value).length > maxLength) {
                        maxLength = String(value).length;
                    }
                });
                column.width = Math.min(maxLength + 2, 50); // Cap at 50 characters
            });

            // Generate the buffer and download
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            this.showAlert({
                message: `Defects report downloaded successfully! Found ${defectsData.length} defects.`,
                type: 'success',
                buttons: [{
                    type: 'success',
                    label: 'Great!',
                }],
            });
            setIsLoading(false);
        } catch (error) {
            console.error('Defects download error:', error);
            setIsLoading(false);
            this.showAlert({
                message: 'An error occurred while downloading the defects file.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        } finally {
            setIsLoading(false);
        }
    }

    handleDownloadMonthly = async (monthlyVehicleChecks, setIsLoading) => {
        if (!monthlyVehicleChecks || monthlyVehicleChecks.length < 1) {
            return;
        } else {
            setIsLoading(true);
            try {
                const fileName = generateFilenameWithRandomString('Monthly_Vehicle_Checks', 'xlsx');
                const formattedData = formatCheckDataForDownload(monthlyVehicleChecks);
    
                // Create a new workbook and worksheet using ExcelJS
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet('Monthly Vehicle Checks');
                
                if (!formattedData || formattedData.length === 0) {
                    throw new Error('No data to export');
                }
                
                // Get headers from the first row of data
                const headers = Object.keys(formattedData[0]);
                
                // Add headers to worksheet
                const headerRow = worksheet.addRow(headers);
                
                // Style the header row
                headerRow.eachCell((cell, colNumber) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFD3D3D3' } // Grey background
                    };
                    cell.font = {
                        bold: true,
                        color: { argb: 'FF000000' } // Black text
                    };
                    cell.alignment = {
                        horizontal: 'center',
                        vertical: 'middle',
                        wrapText: true
                    };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FF000000' } },
                        left: { style: 'thin', color: { argb: 'FF000000' } },
                        bottom: { style: 'thin', color: { argb: 'FF000000' } },
                        right: { style: 'thin', color: { argb: 'FF000000' } }
                    };
                });
                
                // Add data rows
                formattedData.forEach((rowData, rowIndex) => {
                    const row = worksheet.addRow(Object.values(rowData));
                    
                    // Determine the row's overall status for highlighting
                    const statusValue = String(rowData.Status || '').toLowerCase().trim();
                    let rowFillColor = null;
                    
                    if (statusValue === 'failed') {
                        rowFillColor = 'FFFFE6E6'; // Light red background for entire row
                    } else if (statusValue === 'repaired') {
                        rowFillColor = 'FFE6F3FF'; // Light blue background for entire row  
                    } else if (statusValue.includes('inservicedefect') || statusValue.includes('in service defect')) {
                        rowFillColor = 'FFFFFF00'; // Light yellow background for entire row
                    } else if (rowIndex % 2 === 0) {
                        rowFillColor = 'FFF8F9FA'; // Very light grey for even rows
                    }
                    
                    // Style each cell in the data row
                    row.eachCell((cell, colNumber) => {
                        const headerName = headers[colNumber - 1];
                        const cellValue = String(cell.value || '').toLowerCase().trim();
                        
                        // Base styling for all data cells
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                            left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                            bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                            right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
                        };
                        
                        // Apply row-level background color
                        if (rowFillColor) {
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: rowFillColor }
                            };
                        }
                        
                        // Default alignment
                        cell.alignment = {
                            horizontal: 'center',
                            vertical: 'middle',
                            wrapText: false
                        };
                        
                        // Status-based text formatting (keeping the text colors and bold)
                        if (statusValue === 'failed') {
                            cell.font = {
                                color: { argb: 'FFCC0000' }, // Dark red text
                                bold: true
                            };
                        } else if (statusValue === 'repaired') {
                            cell.font = {
                                color: { argb: 'FF0066CC' }, // Dark blue text
                                bold: true
                            };
                        } else if (statusValue.includes('inservicedefect') || statusValue.includes('in service defect')) {
                            cell.font = {
                                color: { argb: 'FFCC9900' }, // Dark yellow/orange text
                                bold: true
                            };
                        } else if (cellValue === '✔️' || cellValue === '✓') {
                            // Center align tick marks with green color
                            cell.font = { color: { argb: 'FF009900' } };
                            cell.alignment.horizontal = 'center';
                        } else if (cellValue === '❌' || cellValue === '✗') {
                            // Center align cross marks with red color
                            cell.font = { color: { argb: 'FFCC0000' } };
                            cell.alignment.horizontal = 'center';
                        }
                        
                        // Column-specific alignments
                        if (headerName === 'Registration' || headerName === 'Driver') {
                            cell.alignment.horizontal = 'left';
                        } else if (headerName === 'Date') {
                            cell.alignment.horizontal = 'center';
                        } else if (headerName === 'Duration' || headerName === 'Mileage') {
                            cell.alignment.horizontal = 'right';
                        } else if (headerName === 'Defects') {
                            cell.alignment.horizontal = 'left';
                            cell.alignment.wrapText = true;
                        }
                    });
                });
                
                // Auto-fit column widths
                worksheet.columns.forEach((column, index) => {
                    const header = headers[index];
                    let maxLength = header.length;
                    
                    // Find the maximum length in this column
                    formattedData.forEach(row => {
                        const cellValue = String(row[header] || '');
                        if (cellValue.length > maxLength) {
                            maxLength = cellValue.length;
                        }
                    });
                    
                    // Set column width (add some padding)
                    column.width = Math.min(Math.max(maxLength + 2, 10), 50);
                });
                
                // Generate Excel buffer
                const buffer = await workbook.xlsx.writeBuffer();
                
                // Create blob and download
                const blob = new Blob([buffer], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
    
                setIsLoading(false);
            } catch (error) {
                console.error('Download error:', error);
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        }
    };
    // ...other methods...
}